import React, { Component } from 'react';
import { Card, Form, Badge, Row, Col, Button, ButtonGroup } from 'react-bootstrap';
import SectionLabel from 'components/controls/sectionLabel';
import { GoPencil } from "react-icons/go";
import Select, { Creatable } from 'components/controls/select';
import { Converter } from "../workspace/converter";
import SessionContext from "../session/context";
import EditMessageContentModal from "../../editor/details/content";
import Utils from "context/utils";

export default class DetailsPane extends Component {
  static contextType = SessionContext;
  constructor(props) {
    super(props);
    this.basicTypes = new Set(["text", "integer", "float"]);
    this.mounted = true;
    this.state = {
      editMessageContentModalShow: false,
      portValue: null
    };
  }

  componentDidMount() {
    this.sessionListener = () => this.forceUpdate();
    if (this.context) {
      this.context.addListener(this.sessionListener);
    }
  }

  componentWillUnmount() {
    if (this.context && this.sessionListener) {
      this.context.removeListener(this.sessionListener);
    }
    this.sessionListener = null;
    this.mounted = false;
  }

  addPortIfNotExists(node, port) {
    if ( !node.data.component["ports"] ) {
      node.data.component["ports"] = [];
    }
    if ( -1 === node.data.component["ports"].indexOf(port) ) {
      node.data.component["ports"].push(port);
    }
  }

  editPortMessageContent(node, port) {
    // Initialize message sub-object if not yet present (initPortValue doesn't set it for message type)
    if ( !port["value"]["message"] ) {
      port["value"]["message"] = { "role": "user", "content": [] };
      port["value"]["message"]["attributes"] = {
        "id": Utils.generateUniqueId(),
        "created_at": Utils.currentTimeUtcIso()
      };
      this.addPortIfNotExists(node, port);
      this.props.shell.shellInterface.markModified();
    }
    this.setState({
      editMessageContentModalShow: true,
      portValue: port["value"]
    });
  }

  updatePortValueFromEvent(node, port, event) {
    const type = port["value"]["type"];
    let value = event.target.value;
    try {
      if ( "text" == type ) {
      } else if ( "integer" == type ) {
        const intValue = parseInt(value);
        value = ( `${intValue}` === value ) ? intValue : value;
      } else if ( "float" == type ) {
        const floatValue = parseFloat(value);
        value = ( `${floatValue}` === value ) ? floatValue : value;
      }
    } catch ( error ) {}
    if ( "text" == type ) {
      port["value"]["text"] = value;
    } else if ( "integer" == type ) {
      port["value"]["integer"] = value;
    } else if ( "float" == type ) {
      port["value"]["float"] = value;
    }
    this.addPortIfNotExists(node, port);
    this.props.shell.shellInterface.markModified();
  }

  // Session objects are picked by alias, but an alias on its own says nothing about what kind of
  // value it holds. Show the object's type beside it, muted and small — the same treatment the
  // Objects section of the playground pane gives it. Deliberately NOT folded into the option's
  // `label`: react-select searches and creates options from that string, so it has to stay the
  // bare alias. Objects whose value hasn't loaded yet (and Creatable's typed-in aliases) simply
  // carry no type, and the annotation is omitted rather than rendered as "unknown".
  formatObjectOption = (option) => (
    <>
      {option.label}
      {option.type &&
        <span className="text-muted small">&nbsp;&middot;&nbsp;{option.type}</span>
      }
    </>
  )

  sessionObjectType(alias) {
    const data = this.context && this.context.sessionData ? this.context.sessionData[alias] : null;
    return ( data && data.type ) || null;
  }

  renderPort(port, node) {
    const header = (
      <Card.Text>
        <Badge variant="secondary" className="title-tag-badge">{port.route}</Badge>
        &nbsp;&nbsp;<span className="text-muted">inline</span>
      </Card.Text>
    );

    if ( !node.data.manifest ) {
      return (
        <>
          {header}
          <Card.Text className="text-muted">No manifest available</Card.Text>
        </>
      );
    }

    const portKey = port["category"] + "-" + ( port["route"] || "default" );
    const portManifest = node.data.manifest["ports_index"][portKey];

    // A stored port whose route is no longer in the manifest (e.g. a port that was removed/deprecated, leaving
    // orphaned values on already-saved components). Rendering it would dereference the missing manifest entry,
    // so skip it rather than crash the details pane.
    if ( !portManifest ) {
      return null;
    }

    if ( this.basicTypes.has(port["value"]["type"]) ) {
      return (
        <>
          {header}
          <Card.Text>
            <Row className="align-items-center">
              {portManifest["value"]["range"] &&
                <Col sm={8}>
                  <Form.Control
                    type="range" size="sm"
                    disabled={!this.props.shell.shellInterface.canEditRevision()}
                    min={portManifest["value"]["range"]["from"]}
                    max={portManifest["value"]["range"]["to"]}
                    step={portManifest["value"]["range"]["step"]}
                    value={Converter.getPortValueData(port) ?? ""}
                    onChange={(event) => {
                      this.updatePortValueFromEvent(node, port, event);
                    }}
                  />
                </Col>
              }
              <Col sm={portManifest["value"]["range"] ? 4 : 12}>
                <Form.Control
                  type="text" size="sm"
                  readOnly={!this.props.shell.shellInterface.canEditRevision()}
                  value={Converter.getPortValueData(port) ?? ""}
                  onChange={(event) => {
                    this.updatePortValueFromEvent(node, port, event);
                  }}
                />
              </Col>
            </Row>
          </Card.Text>
        </>
      );
    }

    if ( "component_alias" == port["value"]["type"] ) {
      let options = [];
      let selectedOption = null;
      for ( const object of ( this.context.sessionObjects || [] ) ) {
        const option = {
          value: object.alias,
          label: object.alias,
          type: this.sessionObjectType(object.alias)
        }
        options.push(option);
        if ( port["value"]["alias"] == object.alias ) {
          selectedOption = option;
        }
      }
      if ( null == selectedOption && port["value"]["alias"] ) {
        selectedOption = {
          value: port["value"]["alias"],
          label: port["value"]["alias"],
          type: this.sessionObjectType(port["value"]["alias"])
        };
      }

      return (
        <>
          {header}
          <Card.Text>
            <Creatable
              options={options}
              formatOptionLabel={this.formatObjectOption}
              isDisabled={!this.props.shell.shellInterface.canEditRevision()}
              value={selectedOption}
              onChange={(option) => {
                port["value"]["alias"] = option.value;
                this.addPortIfNotExists(node, port);
                this.props.shell.shellInterface.markModified();
              }}
            />
          </Card.Text>
        </>
      );
    }

    if ( "workflow_alias" == port["value"]["type"] ) {
      let options = [];
      let selectedOption = null;
      for ( const workflowDefinition of ( this.props.shell.workflowDefinitions || [] ) ) {
        const option = {
          value: workflowDefinition["alias"],
          label: workflowDefinition["alias"]
        }
        options.push(option);
        if ( port["value"]["alias"] == workflowDefinition["alias"] ) {
          selectedOption = option;
        }
      }
      if ( null == selectedOption && port["value"]["alias"] ) {
        selectedOption = {
          value: port["value"]["alias"],
          label: port["value"]["alias"]
        };
      }

      return (
        <>
          {header}
          <Card.Text>
            <Creatable
              options={options}
              isDisabled={!this.props.shell.shellInterface.canEditRevision()}
              value={selectedOption}
              onChange={(option) => {
                port["value"]["alias"] = option.value;
                this.addPortIfNotExists(node, port);
                this.props.shell.shellInterface.markModified();
              }}
            />
          </Card.Text>
        </>
      );
    }

    if ( "choice" == port["value"]["type"] ) {
      let options = [];
      let selectedOption = null;
      for ( const portValueOption of portManifest["value"]["options"] ) {
        const option = {
          value: portValueOption.id,
          label: portValueOption.label
        };
        options.push(option);
        if ( portValueOption.id == port.value.id ) {
          selectedOption = option;
        }
      }

      return (
        <>
          {header}
          <Card.Text>
            <Select
              options={options}
              value={selectedOption}
              isDisabled={!this.props.shell.shellInterface.canEditRevision()}
              onChange={(option) => {
                port["value"]["id"] = option.value;
                this.addPortIfNotExists(node, port);
                this.props.shell.shellInterface.markModified();
              }}
            />
          </Card.Text>
        </>
      );
    }

    if ( "message" == port["value"]["type"] ) {
      return (
        <>
          {header}
          <Card.Text>
            <Row className="align-items-center">
              <Col xs="auto" style={{ "padding-right": "0px" }}>
                Role
              </Col>
              <Col style={{ "padding-right": "0px" }}>
                <Form.Control
                  type="text" size="sm"
                  readOnly={!this.props.shell.shellInterface.canEditRevision()}
                  value={port["value"]["message"] ? port["value"]["message"]["role"] : ""}
                  onChange={(event) => {
                    let value = event.target.value;
                    if ( !port["value"]["message"] ) {
                      port["value"]["message"] = { "role": "", "content": [] };
                    }
                    port["value"]["message"]["role"] = value;
                    this.addPortIfNotExists(node, port);
                    this.props.shell.shellInterface.markModified();
                  }}
                />
              </Col>
              <Col xs="auto" style={{ "padding-right": "0px" }}>
                Content
              </Col>
              <Col xs="auto">
                <ButtonGroup size="sm" className="header-options">
                  <Button variant="light" onClick={(event) => this.editPortMessageContent(node, port)}>
                    <GoPencil/>
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>
          </Card.Text>
        </>
      );
    }

    // A value type with no editor above. Show the row with a muted note rather than nothing: silently
    // rendering an empty fragment is how a renamed port type (workflow_definition_id → workflow_alias)
    // made send_message_workflow disappear from this pane with no clue as to why. Loading now
    // reconciles stored values against the manifest (Converter.normalizeComponentPorts), so this
    // should be unreachable for manifest-backed ports — it exists to fail visibly if that ever slips.
    return (
      <>
        {header}
        <Card.Text className="text-muted">
          No editor for the "{port["value"]["type"]}" value type.
        </Card.Text>
      </>
    );
  }

  renderPorts(label, ports, node) {
    return (
      <>
        <Card.Text as="h6">{label}</Card.Text>
        <hr />
        {ports.map((port, index) => {
          return this.renderPort(port, node);
        })}
      </>
    );
  }

  render() {
    const node = this.props.shell.details.data;

    let displayPorts = [];
    if ( node.data.component["ports"] ) {
      displayPorts = [...node.data.component["ports"]];
    }
    if ( node.data.manifest && node.data.manifest["ports"] ) {
      // Grid widgets don't carry width/height ports — those dimensions come from grid units.
      // Skip the manifest-based synthesis of those rows so the details pane reflects what's
      // actually persisted on the component.
      const isGrid = "grid" === node.data.layoutType;
      const gridSuppressed = new Set(["width", "height"]);
      let existingRoutes = new Set(displayPorts.map(p => p.route));
      for ( const portManifest of node.data.manifest["ports"] ) {
        if ( !existingRoutes.has(portManifest["route"]) && "data_input" == portManifest["category"] ) {
          if ( isGrid && gridSuppressed.has(portManifest["route"]) ) {
            continue;
          }
          let port = {
            "category": "data_input",
            "route": portManifest["route"]
          };
          Converter.initPortValue(port, portManifest);
          displayPorts.push(port);
        }
      }
    }

    return (
      <>
        <Card.Text as="h6">General</Card.Text>
        <hr />
        <SectionLabel>Basics</SectionLabel>
        <Card.Text>
          <table className="properties">
            <tr>
              <td className="prop-name-column">Widget ID</td>
              <td className="prop-value-column"><code>{node.id}</code></td>
            </tr>
            <tr>
              <td className="prop-name-column">Type</td>
              <td className="prop-value-column"><code>{node.data.component["type"]}</code></td>
            </tr>
          </table>
        </Card.Text>
        <Card.Text>
          <Form.Control
            type="text" size="sm"
            readOnly={!this.props.shell.shellInterface.canEditRevision()}
            value={node.data.component["name"]}
            onChange={(event) => {
              node.data.component["name"] = event.target.value;
              this.props.shell.shellInterface.markModified();
            }}
          />
          <Form.Text muted>
            Name displayed for this widget in the playground.
          </Form.Text>
        </Card.Text>

        {this.renderPorts("Data Inputs", displayPorts, node)}

        <EditMessageContentModal
          show={this.state.editMessageContentModalShow}
          onHide={() => this.setState({ editMessageContentModalShow: false })}
          shell={this.props.shell}
          portValue={this.state.portValue}
        />
      </>
    );
  }
}
